/**
 * voice-v8 — Chat-Live-Stream-Client (Cross-Device-Sync, Realtime).
 *
 * EventSource-Wrapper für /api/chat/stream. Empfängt jede chat_messages-
 * Insert (User + Assistant) live und triggert onMessage. Verbindung
 * persistent — bei Drop wird automatisch reconnected mit exponential
 * backoff (1s → 30s).
 *
 * Pre.111 hat den Bootstrap-Pull etabliert: Frontend zieht beim Page-Load
 * die letzten N Messages. Dieser Stream ist der zweite Pfeiler: was
 * NACHEM Bootstrap auf einem anderen Gerät passiert, kommt sofort hier
 * rein. Frontend muss dedupen, weil eine selbst gerade gesendete Message
 * erst durch persistChat → Bus → SSE auch hier landet.
 */

import { AUFTRAG_EREIGNIS, leseAuftrag, type GeraeteAuftrag } from "../../shared/alltag-steuern.js";
import { EINSTELLUNG_EREIGNIS, leseEinstellungsMeldung, type EinstellungsMeldung } from "../../shared/einstellung-aendern.js";
import { SCHMIED_ARBEIT_EREIGNIS, leseSchmiedArbeit, type SchmiedArbeitStand } from "../../shared/schmied-arbeit.js";
import { JOB_FORTSCHRITT_EREIGNIS, leseJobFortschritt, type JobFortschritt } from "../../shared/job-fortschritt.js";
import { SCHMIED_STATUS_EREIGNIS, ZUSATZ_STATUS_EREIGNIS, leseSchmiedStatus, type SchmiedStatusStand } from "../../shared/schmied-status.js";
import { REAKTIONEN_EREIGNIS, leseReaktionen, type ReaktionenStand } from "../../shared/reaktionen.js";
import { AUFTRAEGE_EREIGNIS, leseAuftraegeStand, type AuftraegeStand } from "../../shared/vespera-auftraege.js";
import { ZUSATZ_IDS, type ZusatzId } from "../../shared/entwickler.js";
import {
  STIMME_EREIGNIS,
  leseAufnahmeEreignis,
  leseBesitzEreignis,
  leseFertigEreignis,
  leseStimmeEreignis,
  leseVornEreignis,
  type AufnahmeEreignis,
  type BesitzEreignis,
  type FertigEreignis,
  type StimmeEreignis,
  type VornEreignis,
} from "../../shared/stimme-konto.js";

interface ChatStreamToolCard {
  tool: string;
  callId: string;
  isError: boolean;
  result: unknown;
  anzeige?: unknown;
}

interface ChatStreamMessage {
  role: "user" | "assistant" | "system";
  content: string;
  sessionId: string | null;
  /** ISO-Timestamp wann die Message persisted wurde. */
  createdAt: string;
  /** 15.10.2026: "entwickler" = Beitrag im Dreier-Modus (immer role "system"). */
  source: "voice" | "chat" | "system" | "ptt" | "telegram" | "entwickler";
  /** 16.10.2026: bei „entwickler“ — welcher (shared/entwickler.ts); ungeprüft vom Draht, nur über `entwicklerIdAus` lesen. */
  entwickler?: unknown;
  // Pre.172: Tool-Cards die in der gleichen Bot-Antwort entstanden sind.
  // Frontend rendert sie als Image-Bubble / Tool-Card / etc. — sonst
  // wäre die Bot-Antwort nach Reload nur Text, keine Bilder.
  toolCards?: ChatStreamToolCard[];
  /** Ticket 2796: die Seite, von der ein Sprechtasten-Zug kam. */
  geraet?: string;
  /** 16.10.2026: Bilder einer Nachricht von Martin (Ablage-Adressen, ungeprüft vom Draht) — nur über lib/bild-echo.ts lesen. */
  imageUrls?: unknown;
  imageUrl?: unknown;
}

export interface ChatStreamOptions {
  onMessage: (msg: ChatStreamMessage) => void;
  /**
   * Ticket 2849: ein Auftrag vom Server (alltag_steuern). Nur wer `kann` meldet, bekommt welche.
   */
  onAuftrag?: (auftrag: GeraeteAuftrag) => void;
  /**
   * Ticket 2914: Vespera hat auf Zuruf eine Einstellung geändert. Kommt an JEDE angemeldete Seite des Kontos,
   * ohne `kann`; die Meldung trägt nur Feldnamen. Ohne eigenen Hörer holt der Strom selbst den Serverstand
   * (Abgleich, `gleicheAb`) — die offene Einstellungsseite zeichnet sich danach neu. Nur Tests geben einen eigenen mit.
   */
  onEinstellung?: (meldung: EinstellungsMeldung) => void;
  /**
   * Ticket 2849: was diese Seite kann (`shared/alltag-steuern.ts`, KANN_ALLTAG) — bei jedem
   * (Wieder-)Verbinden neu gefragt, denn das native Plugin steht nach einem Kaltstart erst spät.
   */
  kann?: () => readonly string[];
  /**
   * Ticket 2882: irgendeine Seite dieses Kontos beginnt zu sprechen (auch die eigene —
   * der Empfänger erkennt sie an der Kennung). Siehe shared/stimme-konto.ts.
   */
  onStimme?: (ereignis: StimmeEreignis) => void;
  /** 16.10.2026: eine Seite des Kontos wird benutzt („vorn“) oder ging in den Hintergrund — gleicher Kanal (shared/stimme-konto.ts). */
  onStimmeVorn?: (ereignis: VornEreignis) => void;
  /** 17.10.2026: ein Gerät des Kontos nimmt auf (oder nicht mehr) — gleicher Kanal (shared/stimme-konto.ts „AUFNAHME …“). */
  onStimmeAufnahme?: (ereignis: AufnahmeEreignis) => void;
  /** 17.10.2026: eine Seite des Kontos meldet „Fertig“ (shared/stimme-konto.ts, frontend/lib/seiten-abgleich.ts). */
  onStimmeFertig?: (ereignis: FertigEreignis) => void;
  /** 28.10.2026: die Reihe eines Geräts läuft (oder endete) — gleicher Kanal (shared/stimme-konto.ts „BESITZ“). */
  onStimmeBesitz?: (ereignis: BesitzEreignis) => void;
  /** 16.10.2026: Statusanzeige „Schmied arbeitet“ — Stand (oder text null = aus); kommt auch nach jedem (Neu-)Verbinden. */
  onSchmiedStatus?: (stand: SchmiedStatusStand) => void;
  /** 17.10.2026: „Zimmermann arbeitet“ (shared/schmied-status.ts) — derselbe Aufbau, eigener Stand. */
  onZimmermannStatus?: (stand: SchmiedStatusStand) => void;
  /** 17.10.2026: „<Name> arbeitet / hat deine Nachricht“ je Zusatz-Entwickler (Zimmermann, Hüter) — je ein eigener Stand. */
  onZusatzStatus?: (id: ZusatzId, stand: SchmiedStatusStand) => void;
  /** 18.10.2026: Emoji-Reaktionen der Entwickler an Martins Nachrichten (shared/reaktionen.ts) — der ganze Stand, auch nach jedem Neuverbinden. */
  onReaktionen?: (stand: ReaktionenStand) => void;
  /** 18.10.2026: Aufträge an Vespera (shared/vespera-auftraege.ts) — der ganze Stand, auch nach jedem Neuverbinden. */
  onAuftraege?: (stand: AuftraegeStand) => void;
  /** 19.10.2026: Fortschritt eines Hintergrund-Auftrags dieses Kontos (shared/job-fortschritt.ts) — auch nach jedem Neuverbinden für die laufenden. */
  onJobFortschritt?: (job: JobFortschritt) => void;
  /** Marke „Entwicklung <Zahl>“ (shared/schmied-arbeit.ts). */
  onSchmiedArbeit?: (stand: SchmiedArbeitStand) => void;
  onError?: (info: { event: Event | null; willRetryInMs: number }) => void;
  onOpen?: () => void;
}

interface ChatStreamHandle {
  /** Verbindung beenden, kein Auto-Reconnect mehr. */
  close: () => void;
}

const RECONNECT_MIN_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

export function subscribeChatStream(opts: ChatStreamOptions): ChatStreamHandle {
  let es: EventSource | null = null;
  let closed = false;
  let backoffMs = RECONNECT_MIN_MS;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // 2026-07-04 (Martin): Staleness-Erkennung fuer halb-offene Mobil-Streams.
  let lastActivityAt = Date.now();
  let watchdog: ReturnType<typeof setInterval> | null = null;

  const connect = (): void => {
    if (closed) return;
    const kann = opts.kann?.() ?? [];
    es = new EventSource(
      kann.length > 0 ? `/api/chat/stream?kann=${encodeURIComponent(kann.join(","))}` : "/api/chat/stream",
      { withCredentials: true },
    );

    es.addEventListener(AUFTRAG_EREIGNIS, (raw: MessageEvent<string>) => {
      lastActivityAt = Date.now();
      try {
        const auftrag = leseAuftrag(JSON.parse(raw.data));
        if (auftrag) opts.onAuftrag?.(auftrag);
      } catch (err) {
        console.warn("[chat-stream] bad auftrag frame:", err);
      }
    });

    es.addEventListener(EINSTELLUNG_EREIGNIS, (raw: MessageEvent<string>) => {
      lastActivityAt = Date.now();
      try {
        const meldung = leseEinstellungsMeldung(JSON.parse(raw.data));
        if (meldung) {
          if (opts.onEinstellung) opts.onEinstellung(meldung);
          else void import("./einstellungen-abgleich.js").then((m) => m.gleicheAb());
        }
      } catch (err) {
        console.warn("[chat-stream] bad einstellung frame:", err);
      }
    });

    es.addEventListener(STIMME_EREIGNIS, (raw: MessageEvent<string>) => {
      lastActivityAt = Date.now();
      try {
        const roh: unknown = JSON.parse(raw.data);
        // 28.10.2026: „Besitz“ zuerst — erkennbar an `besitz` (boolean); es trägt nie `nr`, `an`, `aufnahme` oder `fertig`.
        const besitz = leseBesitzEreignis(roh);
        const ereignis = besitz ? null : leseStimmeEreignis(roh);
        if (besitz) opts.onStimmeBesitz?.(besitz);
        else if (ereignis) opts.onStimme?.(ereignis);
        else {
          const vorn = leseVornEreignis(roh);
          if (vorn) opts.onStimmeVorn?.(vorn);
          else {
            const aufnahme = leseAufnahmeEreignis(roh);
            if (aufnahme) opts.onStimmeAufnahme?.(aufnahme);
            else {
              const fertig = leseFertigEreignis(roh);
              if (fertig) opts.onStimmeFertig?.(fertig);
            }
          }
        }
      } catch (err) {
        console.warn("[chat-stream] bad stimme frame:", err);
      }
    });

    es.addEventListener(SCHMIED_STATUS_EREIGNIS, (raw: MessageEvent<string>) => {
      lastActivityAt = Date.now();
      try {
        const stand = leseSchmiedStatus(JSON.parse(raw.data));
        if (stand) opts.onSchmiedStatus?.(stand);
      } catch (err) {
        console.warn("[chat-stream] bad schmied-status frame:", err);
      }
    });

    for (const id of ZUSATZ_IDS) {
      es.addEventListener(ZUSATZ_STATUS_EREIGNIS[id], (raw: MessageEvent<string>) => {
        lastActivityAt = Date.now();
        try {
          const stand = leseSchmiedStatus(JSON.parse(raw.data));
          if (!stand) return;
          if (id === "zimmermann") opts.onZimmermannStatus?.(stand);
          opts.onZusatzStatus?.(id, stand);
        } catch (err) {
          console.warn(`[chat-stream] bad ${id}-status frame:`, err);
        }
      });
    }

    es.addEventListener(REAKTIONEN_EREIGNIS, (raw: MessageEvent<string>) => {
      lastActivityAt = Date.now();
      try {
        const stand = leseReaktionen(JSON.parse(raw.data));
        if (stand) opts.onReaktionen?.(stand);
      } catch (err) {
        console.warn("[chat-stream] bad reaktionen frame:", err);
      }
    });

    es.addEventListener(AUFTRAEGE_EREIGNIS, (raw: MessageEvent<string>) => {
      lastActivityAt = Date.now();
      try {
        const stand = leseAuftraegeStand(JSON.parse(raw.data));
        if (stand) opts.onAuftraege?.(stand);
      } catch (err) {
        console.warn("[chat-stream] bad auftraege frame:", err);
      }
    });

    es.addEventListener(JOB_FORTSCHRITT_EREIGNIS, (raw: MessageEvent<string>) => {
      lastActivityAt = Date.now();
      try {
        const job = leseJobFortschritt(JSON.parse(raw.data));
        if (job) opts.onJobFortschritt?.(job);
      } catch (err) {
        console.warn("[chat-stream] bad job_fortschritt frame:", err);
      }
    });

    es.addEventListener(SCHMIED_ARBEIT_EREIGNIS, (raw: MessageEvent<string>) => {
      lastActivityAt = Date.now();
      try {
        const stand = leseSchmiedArbeit(JSON.parse(raw.data));
        if (stand) opts.onSchmiedArbeit?.(stand);
      } catch (err) {
        console.warn("[chat-stream] bad schmied-arbeit frame:", err);
      }
    });

    es.addEventListener("ready", () => {
      // Server hat den Stream akzeptiert — Backoff resetten.
      backoffMs = RECONNECT_MIN_MS;
      lastActivityAt = Date.now();
      opts.onOpen?.();
    });

    // Beobachtbarer Heartbeat (event: ping) — haelt lastActivityAt frisch,
    // damit der Watchdog eine gesunde, nur-idle Verbindung nicht kappt.
    es.addEventListener("ping", () => {
      lastActivityAt = Date.now();
    });

    es.addEventListener("message", (raw: MessageEvent<string>) => {
      lastActivityAt = Date.now();
      try {
        const data = JSON.parse(raw.data) as ChatStreamMessage;
        if (
          data &&
          (data.role === "user" ||
            data.role === "assistant" ||
            data.role === "system") &&
          typeof data.content === "string" &&
          typeof data.createdAt === "string"
        ) {
          opts.onMessage(data);
        }
      } catch (err) {
        console.warn("[chat-stream] bad message frame:", err);
      }
    });

    es.addEventListener("error", (event: Event) => {
      // EventSource feuert "error" sowohl bei Disconnect als auch wenn der
      // Server den Stream beendet. Wir reconnecten in beiden Fällen — bis
      // zum manuellen close().
      if (closed) return;
      try {
        es?.close();
      } catch {
        /* ignore */
      }
      es = null;
      const wait = backoffMs;
      opts.onError?.({ event, willRetryInMs: wait });
      reconnectTimer = setTimeout(connect, wait);
      backoffMs = Math.min(backoffMs * 2, RECONNECT_MAX_MS);
    });
  };

  connect();

  // 2026-07-04 (Martin): Staleness-Watchdog. Auf Mobil stirbt SSE oft
  // "halb offen" (kein FIN) — EventSource merkt das lange nicht und der
  // Cross-Device-Sync schlaeft still ein. Kommt >60s nichts (2+ Pings
  // verpasst), erzwingen wir einen Reconnect.
  watchdog = setInterval(() => {
    if (closed) return;
    if (Date.now() - lastActivityAt > 60_000) {
      lastActivityAt = Date.now();
      try {
        es?.close();
      } catch {
        /* ignore */
      }
      es = null;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      backoffMs = RECONNECT_MIN_MS;
      connect();
    }
  }, 20_000);

  return {
    close: () => {
      closed = true;
      if (watchdog) {
        clearInterval(watchdog);
        watchdog = null;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      try {
        es?.close();
      } catch {
        /* ignore */
      }
      es = null;
    },
  };
}
